import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "@tanstack/react-router";
import type { Lang } from "./types";
import { translate, type TranslationKey } from "./translations";
import { useStorefront } from "./storefront";
import { resolveStorefrontLanguage } from "./language";

const STORAGE_KEY = "emynor.storefront.locale";

interface I18nValue {
  lang: Lang;
  dir: "rtl" | "ltr";
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const storefront = useStorefront();
  const defaultLanguage: Lang =
    storefront.tenant.defaultLocale === "ar" ? "ar" : "en";
  const [lang, setLangState] = useState<Lang>(defaultLanguage);

  useEffect(() => {
    const stored =
      typeof window !== "undefined"
        ? window.localStorage.getItem(STORAGE_KEY)
        : null;
    const next = resolveStorefrontLanguage({
      defaultLocale: defaultLanguage,
      enabledLocales: storefront.tenant.enabledLocales,
      storedLocale: localeFromUrl() ?? stored,
    });
    setLangState(next);
  }, [defaultLanguage, storefront.tenant.enabledLocales]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const setLang = useCallback(
    (next: Lang) => {
      if (!storefront.tenant.enabledLocales.includes(next)) return;
      setLangState(next);
      const url = new URL(router.state.location.href, window.location.origin);
      url.searchParams.set("lang", next);
      void router.navigate({ href: `${url.pathname}${url.search}${url.hash}`, replace: true, resetScroll: false });
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
    },
    [router, storefront.tenant.enabledLocales],
  );

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => {
      return translate(key, lang, vars);
    },
    [lang],
  );

  const value = useMemo<I18nValue>(
    () => ({ lang, dir: lang === "ar" ? "rtl" : "ltr", setLang, t }),
    [lang, setLang, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function localeFromUrl(): Lang | null {
  if (typeof window === "undefined") return null;
  const value = new URL(window.location.href).searchParams.get("lang");
  return value === "en" || value === "ar" ? value : null;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
